import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  ShieldCheck,
  ShieldAlert,
  Clock,
  Building2,
  X,
  FileText,
  Phone,
  MapPin,
  Globe,
  Calendar,
  AlertCircle,
  CheckCircle2,
  Save,
  MessageSquare,
  Sparkles,
  Ban,
  Check,
} from 'lucide-react';
import type { User } from '../../../../shared/types/index.js';

interface PublisherVerificationModalAdminProps {
  user: User | null;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (status: 'verified' | 'rejected' | 'pending' | 'unverified', notes?: string) => void;
  isPending: boolean;
}

export const PublisherVerificationModalAdmin: React.FC<PublisherVerificationModalAdminProps> = ({
  user,
  isOpen,
  onClose,
  onSubmit,
  isPending,
}) => {
  const [selectedStatus, setSelectedStatus] = useState<'verified' | 'rejected' | 'pending' | 'unverified'>('verified');
  const [notes, setNotes] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      const currentStatus = user.publisherVerificationStatus || (user.isPublisherVerified ? 'verified' : 'unverified');
      setSelectedStatus(currentStatus as any);
      setNotes(user.publisherVerificationNotes || '');
      setValidationError(null);
    }
  }, [user, isOpen]);

  if (!isOpen || !user || typeof document === 'undefined') return null;

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);

    if (selectedStatus === 'rejected' && !notes.trim()) {
      setValidationError('Wajib mengisi catatan/alasan penolakan agar penerbit mengetahui data yang perlu diperbaiki.');
      return;
    }

    onSubmit(selectedStatus, notes.trim());
  };

  const handleQuickApprove = () => {
    setSelectedStatus('verified');
    const noteText = notes.trim() || 'Dokumen legalitas dan identitas penerbit terverifikasi resmi oleh Admin UIBN.';
    setNotes(noteText);
    setValidationError(null);
    onSubmit('verified', noteText);
  };

  const handleQuickReject = () => {
    setSelectedStatus('rejected');
    if (!notes.trim()) {
      setNotes('NIB atau dokumen legalitas operasional belum lengkap. Silakan periksa kembali.');
    }
    setValidationError(null);
  };

  const rejectionTemplates = [
    'Nomor Induk Berusaha (NIB) / SK Notaris tidak valid atau belum terdaftar resmi di OSS.',
    'Alamat kantor operasional penerbit tidak lengkap atau tidak dapat diverifikasi.',
    'Nomor kontak redaksi/telepon operasional tidak aktif atau tidak dapat dihubungi.',
    'Nama legal badan usaha tidak sesuai dengan akta pendirian/dokumen resmi.',
    'Format berkas legalitas belum lengkap, silakan unggah pembaharuan data resmi.',
  ];

  const approvalTemplates = [
    'Dokumen legalitas NIB dan profil penerbit terverifikasi resmi dan sah.',
    'Verifikasi identitas penerbit disetujui. Akun diaktifkan untuk menerbitkan UIBN.',
  ];

  const statusOptions = [
    {
      value: 'verified',
      label: 'Terima & Verifikasi Resmi (Aktif)',
      desc: 'Penerbit berhak mendaftarkan buku di UIBN dan menerima naskah penulis.',
      color: 'border-emerald-500 bg-emerald-50 text-emerald-950',
      icon: CheckCircle2,
      badgeColor: 'bg-emerald-600 text-white',
    },
    {
      value: 'rejected',
      label: 'Tolak / Perlu Perbaikan Data',
      desc: 'Penolakan dengan catatan wajib. Penerbit dapat memperbaiki data dan mengajukan ulang.',
      color: 'border-rose-500 bg-rose-50 text-rose-950',
      icon: ShieldAlert,
      badgeColor: 'bg-rose-600 text-white',
    },
    {
      value: 'pending',
      label: 'Menunggu Peninjauan (Pending)',
      desc: 'Dokumen legalitas sedang dalam antrean verifikasi oleh tim kurator.',
      color: 'border-amber-500 bg-amber-50 text-amber-950',
      icon: Clock,
      badgeColor: 'bg-amber-600 text-white',
    },
    {
      value: 'unverified',
      label: 'Belum Terverifikasi (Baru)',
      desc: 'Akun baru belum melengkapi berkas legalitas penerbit.',
      color: 'border-slate-400 bg-slate-50 text-slate-900',
      icon: AlertCircle,
      badgeColor: 'bg-slate-600 text-white',
    },
  ];

  return createPortal(
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-950/70 backdrop-blur-xs">
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-3xl max-w-2xl w-full border border-slate-200 shadow-2xl overflow-hidden max-h-[92vh] flex flex-col relative"
        >
          {/* Header */}
          <div className="p-5 sm:p-6 border-b border-slate-100 bg-gradient-to-r from-purple-900 via-indigo-900 to-slate-900 text-white flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/20 text-purple-200">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-bold text-white text-base">Kelola Verifikasi Penerbit</h3>
                <p className="text-xs text-purple-200/90 mt-0.5">
                  Admin Panel • Tinjau Data Legalitas, Terima / Tolak Pengajuan
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Tutup modal"
              className="p-1.5 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body Content */}
          <form onSubmit={handleFormSubmit} className="p-5 sm:p-6 overflow-y-auto space-y-5 flex-1 text-slate-700">
            {/* Publisher Identity Summary */}
            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                    Penerbit / Organisasi
                  </span>
                  <p className="font-bold text-slate-900 text-sm sm:text-base flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-purple-600 shrink-0" />
                    <span>{user.organization || user.name}</span>
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono bg-purple-100 text-purple-800 border border-purple-200 px-2 py-0.5 rounded-lg font-bold">
                    Kode: {user.publisherCode || 'Auto'}
                  </span>
                  <span className="text-xs text-slate-500 font-mono">
                    ID: {user.id.substring(0, 10)}...
                  </span>
                </div>
              </div>

              {/* Legal Info Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-2 border-t border-slate-200/80 text-xs">
                <div className="bg-white p-2.5 rounded-xl border border-slate-200/80">
                  <span className="text-[10px] text-slate-400 font-semibold block flex items-center gap-1">
                    <FileText className="w-3 h-3 text-slate-400" />
                    Nama Legal Badan Usaha:
                  </span>
                  <span className="font-medium text-slate-800">
                    {user.publisherLegalName || user.organization || '-'}
                  </span>
                </div>

                <div className="bg-white p-2.5 rounded-xl border border-slate-200/80">
                  <span className="text-[10px] text-slate-400 font-semibold block flex items-center gap-1">
                    <FileText className="w-3 h-3 text-slate-400" />
                    NIB / SK Notaris:
                  </span>
                  <span className="font-mono font-medium text-slate-800">
                    {user.publisherNib || '(Belum dicantumkan)'}
                  </span>
                </div>

                <div className="bg-white p-2.5 rounded-xl border border-slate-200/80">
                  <span className="text-[10px] text-slate-400 font-semibold block flex items-center gap-1">
                    <Phone className="w-3 h-3 text-slate-400" />
                    No. Telepon / Redaksi:
                  </span>
                  <span className="font-medium text-slate-800">
                    {user.publisherPhone || user.email}
                  </span>
                </div>

                <div className="bg-white p-2.5 rounded-xl border border-slate-200/80">
                  <span className="text-[10px] text-slate-400 font-semibold block flex items-center gap-1">
                    <Globe className="w-3 h-3 text-slate-400" />
                    Website / Media Sosial:
                  </span>
                  {user.publisherWebsite ? (
                    <a
                      href={user.publisherWebsite}
                      target="_blank"
                      rel="noreferrer"
                      className="text-purple-600 hover:text-purple-800 underline font-mono truncate block"
                    >
                      {user.publisherWebsite}
                    </a>
                  ) : (
                    <span className="text-slate-400">-</span>
                  )}
                </div>

                {user.publisherAddress && (
                  <div className="sm:col-span-2 bg-white p-2.5 rounded-xl border border-slate-200/80">
                    <span className="text-[10px] text-slate-400 font-semibold block flex items-center gap-1">
                      <MapPin className="w-3 h-3 text-slate-400" />
                      Alamat Kantor Operasional:
                    </span>
                    <span className="font-medium text-slate-800">
                      {user.publisherAddress}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Action Decision Buttons */}
            <div className="p-3.5 rounded-2xl bg-indigo-50/50 border border-indigo-100 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="text-xs">
                <span className="font-bold text-slate-900 block">Tindakan Cepat Verifikasi:</span>
                <span className="text-slate-500 text-[11px]">Pilih keputusan atau gunakan template di bawah</span>
              </div>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={handleQuickApprove}
                  disabled={isPending}
                  className="flex-1 sm:flex-none px-3.5 py-2 rounded-xl text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs transition flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span>Terima Langsung</span>
                </button>
                <button
                  type="button"
                  onClick={handleQuickReject}
                  className="flex-1 sm:flex-none px-3.5 py-2 rounded-xl text-xs font-bold bg-rose-600 hover:bg-rose-700 text-white shadow-xs transition flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <Ban className="w-3.5 h-3.5" />
                  <span>Tolak & Beri Catatan</span>
                </button>
              </div>
            </div>

            {/* Status Selection Cards */}
            <div className="space-y-2">
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                Pilih Status Verifikasi:
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {statusOptions.map((opt) => {
                  const Icon = opt.icon;
                  const isSelected = selectedStatus === opt.value;
                  return (
                    <div
                      key={opt.value}
                      onClick={() => {
                        setSelectedStatus(opt.value as any);
                        setValidationError(null);
                      }}
                      className={`p-3.5 rounded-2xl border-2 transition cursor-pointer flex flex-col justify-between ${
                        isSelected
                          ? `${opt.color} shadow-xs ring-2 ring-purple-500/20`
                          : 'border-slate-200 bg-white hover:border-slate-300 text-slate-600'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${isSelected ? 'text-purple-700' : 'text-slate-400'}`} />
                          <span className="text-xs font-bold">{opt.label}</span>
                        </div>
                        <input
                          type="radio"
                          name="verificationStatus"
                          value={opt.value}
                          checked={isSelected}
                          onChange={() => {
                            setSelectedStatus(opt.value as any);
                            setValidationError(null);
                          }}
                          className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                        />
                      </div>
                      <p className="text-[11px] text-slate-500 leading-tight">
                        {opt.desc}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Admin Notes & Quick Templates */}
            <div className="space-y-2">
              <label className="block text-xs font-bold text-slate-700 flex items-center justify-between">
                <span className="flex items-center gap-1.5">
                  <MessageSquare className="w-3.5 h-3.5 text-slate-400" />
                  <span>
                    Catatan Verifikasi Admin {selectedStatus === 'rejected' ? <span className="text-rose-600 font-bold">(Wajib Diisi untuk Penolakan)*</span> : '(Opsional)'}:
                  </span>
                </span>
                <span className="text-[10px] text-slate-400 font-normal">
                  Penerbit akan menerima catatan ini
                </span>
              </label>

              <textarea
                rows={3}
                value={notes}
                onChange={(e) => {
                  setNotes(e.target.value);
                  if (validationError) setValidationError(null);
                }}
                placeholder={
                  selectedStatus === 'rejected'
                    ? 'Tuliskan alasan penolakan dan instruksi perbaikan data bagi penerbit...'
                    : 'Misal: Dokumen legalitas NIB dan badan usaha telah diverifikasi sah...'
                }
                className={`w-full px-3.5 py-2.5 rounded-xl border text-xs transition bg-white ${
                  validationError
                    ? 'border-rose-300 focus:ring-2 focus:ring-rose-500/20 focus:border-rose-600 bg-rose-50/20'
                    : 'border-slate-200 focus:outline-hidden focus:ring-2 focus:ring-purple-500/20 focus:border-purple-600'
                }`}
              />

              {validationError && (
                <div className="text-[11px] text-rose-600 flex items-center gap-1.5 font-medium animate-in fade-in duration-150">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                  <span>{validationError}</span>
                </div>
              )}

              {/* Template suggestions */}
              <div className="space-y-1.5 pt-1">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                  Template Cepat Catatan:
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {(selectedStatus === 'rejected' ? rejectionTemplates : approvalTemplates).map((template, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => {
                        setNotes(template);
                        setValidationError(null);
                      }}
                      className="px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-[10px] font-medium transition cursor-pointer text-left"
                    >
                      + {template.substring(0, 45)}...
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer Buttons */}
            <div className="flex items-center justify-end gap-2.5 pt-4 border-t border-slate-100">
              <button
                type="button"
                onClick={onClose}
                disabled={isPending}
                className="px-4 py-2 rounded-xl text-xs font-medium text-slate-600 hover:bg-slate-100 transition cursor-pointer"
              >
                Batal
              </button>

              <button
                type="submit"
                disabled={isPending}
                className={`px-5 py-2.5 rounded-xl text-xs font-bold text-white shadow-xs flex items-center gap-1.5 cursor-pointer disabled:opacity-50 transition ${
                  selectedStatus === 'verified'
                    ? 'bg-emerald-600 hover:bg-emerald-700'
                    : selectedStatus === 'rejected'
                    ? 'bg-rose-600 hover:bg-rose-700'
                    : 'bg-purple-700 hover:bg-purple-800'
                }`}
              >
                <Save className="w-3.5 h-3.5" />
                <span>{isPending ? 'Memproses...' : 'Simpan Keputusan Verifikasi'}</span>
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
};
